import { useRef, type PointerEvent } from 'react';
import { prefersReducedMotion, useCountFromZero } from '../../lib/motion';

interface AccessPassProps {
  name: string;
  memberNo: number | null;
  roleLabel: string;
}

/**
 * The early-access ticket. It follows the pointer — tilt plus a sheen that sits
 * where the light would — because the first thing a person does with a ticket
 * is turn it over in their hand.
 */
export default function AccessPass({ name, memberNo, roleLabel }: AccessPassProps) {
  const ref = useRef<HTMLDivElement>(null);
  const shown = useCountFromZero(memberNo ?? 0, memberNo !== null, 1800);

  const onMove = (event: PointerEvent<HTMLDivElement>) => {
    const node = ref.current;
    if (!node || event.pointerType === 'touch' || prefersReducedMotion()) return;
    const box = node.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width;
    const y = (event.clientY - box.top) / box.height;
    node.style.setProperty('--rx', `${(x - 0.5) * 16}deg`);
    node.style.setProperty('--ry', `${(0.5 - y) * 12}deg`);
    node.style.setProperty('--mx', `${x * 100}%`);
    node.style.setProperty('--my', `${y * 100}%`);
  };

  const onLeave = () => {
    const node = ref.current;
    if (!node) return;
    node.style.setProperty('--rx', '0deg');
    node.style.setProperty('--ry', '0deg');
  };

  return (
    <div className="pass-wrap">
      <div ref={ref} className="pass" onPointerMove={onMove} onPointerLeave={onLeave}>
        <div className="pass-main">
          <Reel />
          <div className="pass-top">
            <span className="pass-brand">
              <svg className="pass-mark" viewBox="20 10 80 100" aria-hidden="true">
                <g fill="currentColor" stroke="currentColor" strokeWidth="11" strokeLinejoin="round">
                  <polygon points="27,44 30,41 30,81 27,84" />
                  <polygon points="52,20 57,17 57,100 52,103" />
                  <polygon points="78,45 78,76 93,60.5" />
                </g>
              </svg>
              Frameon
            </span>
            <span className="mono">Early Access</span>
          </div>

          <div className="pass-k">Creator</div>
          <div className="pass-no" aria-label={memberNo ? `Creator number ${memberNo}` : 'Creator number pending'}>
            {memberNo ? `#${String(shown).padStart(4, '0')}` : '#————'}
          </div>

          <div className="pass-name">{name}</div>
          <div className="pass-foot">
            <span className="pass-status">
              <b aria-hidden="true" /> Under review
            </span>
            <span className="mono">{roleLabel}</span>
          </div>
        </div>

        <div className="pass-stub" aria-hidden="true">
          <span className="pass-admit">Admit one</span>
          <Barcode seed={memberNo ?? 7} />
          <span className="pass-admit">Wave 01</span>
        </div>

        <div className="pass-sheen" aria-hidden="true" />
      </div>
    </div>
  );
}

/** A film reel, half off the edge of the ticket, turning slowly. */
function Reel() {
  const holes = [0, 72, 144, 216, 288];
  return (
    <svg className="pass-reel" viewBox="0 0 200 200" aria-hidden="true">
      <g className="reel-spin">
        <circle cx="100" cy="100" r="92" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="100" cy="100" r="80" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 6" />
        {holes.map(angle => (
          <circle
            key={angle}
            cx={100 + 50 * Math.cos((angle * Math.PI) / 180)}
            cy={100 + 50 * Math.sin((angle * Math.PI) / 180)}
            r="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
        ))}
        <circle cx="100" cy="100" r="12" fill="currentColor" />
      </g>
    </svg>
  );
}

/** Bars derived from the member number, so every ticket's code is its own. */
function Barcode({ seed }: { seed: number }) {
  let state = seed * 9301 + 49297;
  const next = () => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };

  const bars: { y: number; h: number }[] = [];
  let y = 0;
  while (y < 118) {
    const h = 1 + Math.floor(next() * 4);
    bars.push({ y, h });
    y += h + 1 + Math.floor(next() * 3);
  }

  return (
    <svg className="pass-code" viewBox="0 0 30 120" preserveAspectRatio="none">
      {bars.map(bar => (
        <rect key={bar.y} x="0" y={bar.y} width="30" height={bar.h} fill="currentColor" />
      ))}
    </svg>
  );
}
